import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";

function ReviewRow({
  label,
  value,
  done = true,
}: {
  label: string;
  value: string;
  done?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      {done ? (
        <Check className="h-3 w-3 shrink-0 text-success-subtle-foreground" aria-hidden="true" />
      ) : (
        <span className="h-3 w-3 shrink-0 rounded-full border border-border" aria-hidden="true" />
      )}
      <span className="flex-1 text-[11px] text-foreground">{label}</span>
      <span className="text-[11px] text-muted-foreground">{value}</span>
    </div>
  );
}

export function NewOrgReadyToCreateAside({
  name,
  productLineLabel,
  industriesSummary,
  marketsSummary,
  defaultBrand,
  accentLabel,
}: {
  name: string;
  productLineLabel: string;
  industriesSummary: string;
  marketsSummary: string;
  defaultBrand: string;
  accentLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <aside className="hidden w-[280px] shrink-0 flex-col gap-3 border-l border-border bg-muted px-5 py-6 lg:flex">
      <p className="font-mono text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
        {t("iam.newOrgDialog.aside.readyHeading", "Ready to create")}
      </p>
      <div className="flex flex-col gap-2.5">
        <ReviewRow
          label={t("iam.newOrgDialog.aside.reviewName", "Name")}
          value={name}
        />
        <ReviewRow
          label={t("iam.newOrgDialog.productLineLabel", "Product line")}
          value={productLineLabel}
        />
        <ReviewRow
          label={t("iam.newOrgDialog.industriesLabel", "Industries")}
          value={industriesSummary}
        />
        <ReviewRow
          label={t("iam.newOrgDialog.marketsLabel", "Pre Flight markets")}
          value={marketsSummary}
        />
        <ReviewRow
          label={t("iam.newOrgDialog.defaultBrandLabel", "Default brand")}
          value={defaultBrand}
        />
        <ReviewRow
          label={t("iam.newOrgDialog.aside.reviewLogoAccent", "Logo and accent")}
          value={accentLabel}
        />
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {t("iam.newOrgDialog.aside.editStepsHint", "Click a finished step above to change it.")}
      </p>
    </aside>
  );
}

/** The summary aside repeats the Review step, so narrow screens drop it and give the form the full width. */
export function NewOrgDrawerBodyLayout({
  children,
  aside,
}: {
  children: ReactNode;
  aside: ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-6 sm:px-8">{children}</div>
      {aside}
    </div>
  );
}

export function NewOrgSectionHeading({
  title,
  hint,
  trailing,
}: {
  title: string;
  hint?: string;
  trailing?: ReactNode;
}) {
  return (
    <div className={cn("flex flex-col gap-0.5", trailing && "flex-row items-end justify-between gap-2")}>
      <div className="flex min-w-0 flex-col gap-0.5">
        <h4 className="text-[13px] font-semibold text-foreground">{title}</h4>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      {trailing}
    </div>
  );
}
